import { useEffect, useState } from 'react';

/* CSS */
import shared from '../styles/shared.module.css';
import styles from './TripsSummaryPage.module.css';

/* Contexts */
import { useAuthContext } from '../context/AuthContext';

/* Utils */
import { calculateCostStats, calculateTravelStats, calculateTravelStatsDetailed } from '../utils/statsUtils';
import { getCountryImage } from '../utils/countryUtils';
import { tripUtils } from '../utils/tripUtils';
import { getMostExpensiveCategory } from '../utils/costUtils';

/* Card components */
import StatCard from '../components/cards/StatCard';
import SpendingCard from '../components/cards/SpendingCard';
import CostSummaryCard from '../components/cards/CostSummaryCard';

/* Stat details */
import TripsPlannedDetail from '../components/cards/statDetails/TripsPlannedDetail';
import DaysTraveledDetail from '../components/cards/statDetails/DaysTraveledDetail';
import CountriesDetail from '../components/cards/statDetails/CountriesDetail';
import BestBuddyDetail from '../components/cards/statDetails/BestBuddyDetail';
import MostActiveMonthDetail from '../components/cards/statDetails/MostActiveMonthDetail';

/* Stat card icons */
import itineraries_planned from "../assets/icons/itineraries_planned.svg";
import days_traveled from "../assets/icons/days_traveled.svg";
import top_country from "../assets/icons/top_country.svg";
import top_month from "../assets/icons/top_month.svg";
import best_buddy from "../assets/icons/best_buddy.svg";

/* Background images */
import your_trips_bg from "../assets/backgrounds/your_trips_summary.png";
import your_spending_bg from "../assets/backgrounds/your_spending_summary.png";

const TripsSummaryPage = () => {

    const { currentUser } = useAuthContext();

    const [stats, setStats] = useState(null);
    const [costStats, setCostStats] = useState(null);
    const [allTrips, setAllTrips] = useState([]);
    const [topCategory, setTopCategory] = useState(null);
    const [detailed, setDetailed] = useState(null);

    useEffect(() => {
        const fetchTrips = async () => {

            // Fetch trips from storage
            const trips = await tripUtils.getAllTrips();
            const tripsList = JSON.parse(JSON.stringify([...trips]));

            setStats(calculateTravelStats(tripsList, currentUser));
            setCostStats(calculateCostStats(tripsList));

            setAllTrips(tripsList);
            setTopCategory(getMostExpensiveCategory(tripsList));
            setDetailed(calculateTravelStatsDetailed(tripsList, currentUser));
        };

        fetchTrips();
    }, []);

    if (!stats || !costStats || !detailed) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            {/* Page header */}
            <section className={shared.heroHeader}>
                <div>
                    {/* Page title */}
                    <h1 className={shared.heroTitle}>Trips Summary</h1>
                    <p className={shared.heroSubtitle}>Let’s look back at your adventures</p>
                </div>
            </section>

            {/* ==== SECTION 1: YOUR TRIPS (Bento Grid) ==== */}
            <section className={shared.section}>

                {/* Section header */}
                <div className={shared.sectionHeader}>
                    <h2 className={shared.sectionTitle}>Your trips</h2>
                </div>
                <p className={shared.sectionSubtitle}>Statistics on all the trips you have taken</p>

                <img src={your_trips_bg} className={`${styles.bgImage} ${styles.yourTripsBg}`} alt="Your Trips Background" />

                {/* Bento grid */}
                <div className={styles.bentoGrid}>
                    <StatCard
                        label="Trips Planned"
                        value={stats ? stats.totalTrips : '-'}
                        icon={itineraries_planned}
                        info="Total number of itineraries you have created in TravelBuddy"
                        back={<TripsPlannedDetail trips={detailed.tripsPlanned} />}
                    />
                    <StatCard
                        className={styles.spanRow2}
                        label="Most Visited Country"
                        value={stats ? stats.mostVisitedCountry : '-'}
                        icon={top_country}
                        note={stats.mostVisitedCountry === '-' ? '(No countries visited yet!)' : ''}
                        centered={stats.mostVisitedCountry === '-'}
                        info="The country you have traveled to the most"
                        back={<CountriesDetail countries={detailed.countries} />}
                    >
                        {stats.mostVisitedCountry !== '-' && (
                            <img
                                src={getCountryImage(stats.mostVisitedCountry)}
                                className={styles.mostVisitedCountryImage}
                                alt={stats.mostVisitedCountry}
                                loading="lazy"
                                decoding="async"
                            />
                        )}
                    </StatCard>
                    <StatCard
                        label="Most Active Month"
                        value={stats ? stats.mostActiveMonth : '-'}
                        icon={top_month}
                        info="Month with the highest travel activity"
                        back={<MostActiveMonthDetail months={detailed.months} maxDays={detailed.maxMonthDays} />}
                    />
                    <StatCard
                        label="Days Traveled"
                        value={stats ? stats.totalDays : '-'}
                        icon={days_traveled}
                        info="Total number of days you have traveled across all your trips"
                        back={<DaysTraveledDetail rows={detailed.perTripDays} total={detailed.totalDaysClamped} />}
                    />
                    <StatCard
                        label="Best TravelBuddy"
                        value={stats ? stats.bestBuddy : '-'}
                        icon={best_buddy}
                        note={stats.bestBuddy === '-' ? '(Seems like you\'re a solo traveler!)' : ''}
                        info="Your most frequent travel companion"
                        back={<BestBuddyDetail buddies={detailed.buddies} />}
                    />
                </div>
            </section>

            {/* ==== SECTION 2: YOUR SPENDING (Split Layout) ==== */}
            <section className={shared.section}>

                {/* Section header */}
                <div className={shared.sectionHeader}>
                    <h2 className={shared.sectionTitle}>Your spending</h2>
                </div>
                <p className={shared.sectionSubtitle}>An overview of your travel expenses</p>

                <img src={your_spending_bg} className={`${styles.bgImage} ${styles.yourSpendingBg}`} alt="Your Spending Background" loading="lazy" decoding="async" />

                {/* Spending container */}
                <div className={styles.spendingContainer}>

                    {/* LEFT COLUMN: Total Spending Card */}
                    <CostSummaryCard
                        totalCost={costStats?.totalSpending || 0}
                        trips={allTrips}
                    />
                    {/* RIGHT COLUMN: Stack of 3 cards */}
                    <div className={styles.spendingRightColumn}>
                        <SpendingCard
                            label="Most Expensive Trip"
                            value={costStats ? costStats.mostExpensiveTrip.totalCost : '-'}
                            subtitle={costStats ? costStats.mostExpensiveTrip.title : ''}
                            tripId={costStats ? costStats.mostExpensiveTrip.id : ''}
                            info="The trip where you spent the most money"
                        />
                        <SpendingCard
                            label="Least Expensive Trip"
                            value={costStats ? costStats.leastExpensiveTrip.totalCost : '-'}
                            subtitle={costStats ? costStats.leastExpensiveTrip.title : ''}
                            tripId={costStats ? costStats.leastExpensiveTrip.id : ''}
                            info="The trip where you spent the least money"
                        />
                        <SpendingCard
                            label="Most Expensive Category"
                            value={topCategory ? topCategory.total : 0}
                            subtitle={topCategory ? topCategory.category : '-'}
                            tripId={topCategory ? topCategory.id : ''}
                            info="The category where you spent the most money"
                        />
                    </div>
                </div>
            </section>
        </div>
    );
};

export default TripsSummaryPage;